/**
 * Contact form fields and their reader. See lead-form.tsx for why the two are
 * co-located and why the reader gates on `form.has()`.
 */

import type { Contact, Organization } from '@erp/contracts'
import { text } from '../forms'
import { useOwnerOptions } from '../status-context'
import { Field, Select, TextInput } from '../ui'

export function readContactForm(form: FormData): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  const put = (key: string, value: unknown) => {
    if (form.has(key)) fields[key] = value
  }

  put('firstName', text(form, 'firstName'))
  put('lastName', text(form, 'lastName'))
  put('email', text(form, 'email'))
  put('phone', text(form, 'phone'))
  put('mobile', text(form, 'mobile'))
  put('jobTitle', text(form, 'jobTitle'))
  put('organizationId', text(form, 'organizationId'))
  put('ownerId', text(form, 'ownerId'))

  return fields
}

export function ContactFields({
  contact,
  organizations,
  presetOrganizationId,
  issues,
}: {
  contact?: Contact | undefined
  organizations: readonly Organization[]
  presetOrganizationId?: string | null
  issues: Record<string, string[]>
}) {
  const selectedOrganization = contact?.organizationId ?? presetOrganizationId ?? ''
  const owners = useOwnerOptions(contact?.ownerId ?? null)

  return (
    <>
      <fieldset className="ax-fieldset">
        <legend>Person</legend>
        <Field label="First name" error={issues['firstName']}>
          <TextInput name="firstName" defaultValue={contact?.firstName} />
        </Field>
        <Field label="Last name" error={issues['lastName']}>
          <TextInput name="lastName" defaultValue={contact?.lastName} />
        </Field>
        <Field label="Email" error={issues['email']}>
          <TextInput name="email" type="email" defaultValue={contact?.email} />
        </Field>
        <Field label="Phone" error={issues['phone']}>
          <TextInput name="phone" type="tel" defaultValue={contact?.phone} />
        </Field>
        <Field label="Mobile" error={issues['mobile']}>
          <TextInput name="mobile" type="tel" defaultValue={contact?.mobile} />
        </Field>
        <Field label="Job title" error={issues['jobTitle']}>
          <TextInput name="jobTitle" defaultValue={contact?.jobTitle} />
        </Field>
        <Field label="Owner" error={issues['ownerId']}>
          <Select name="ownerId" value={contact?.ownerId ?? ''} options={owners} />
        </Field>
      </fieldset>

      <fieldset className="ax-fieldset">
        <legend>Organization</legend>
        <Field label="Organization" error={issues['organizationId']}>
          {/*
            A plain select, capped at the first 200 organizations by name. Past
            that this needs a search-backed combobox — which is a dependency, so
            it waits until the list actually outgrows the dropdown.
          */}
          <select className="ax-select" name="organizationId" defaultValue={selectedOrganization}>
            <option value="">None</option>
            {organizations.map((organization) => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </Field>
      </fieldset>
    </>
  )
}
